import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, filter, finalize, map, takeUntil, takeWhile, tap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';

import {ProfileService} from '../../../../services/profile.service';
import {Experience} from '../../../../models/experience';
import {CertificatesService} from '../../../../services/certificates.service';
import {ClientMessageService} from '../../../../services/client-message.service';
import {ProfileEntity} from '@models/user/profile.entity';

@Component({
  selector: 'app-user-experience-list',
  templateUrl: './user-experience-list.component.html',
  styleUrls: ['./user-experience-list.component.scss'],
})
export class UserExperienceListComponent implements OnInit, OnDestroy {

  trainer: ProfileEntity;
  cetrificates: Experience[] = [];
  certificatesFormArray: UntypedFormArray;
  formName = 'experiences';
  maxlength = 500;

  public canEditProfile: boolean;
  private destroy$ = new Subject<void>();
  private subs: Subscription;

  get isTrainer() {
    return this.profileService.isProfile('TRAINER_TYPE');
  }

  get isPartner() {
    return this.profileService.isProfile('PARTNER_TYPE');
  }

  constructor(
    private profileService: ProfileService,
    private certificateService: CertificatesService,
    private clientMessageService: ClientMessageService,
  ) {
    this.canEditProfile = true;
    this.subs = new Subscription();
  }

  ngOnInit() {
    this.subs.add(
      this.profileService.editProfileEvent.subscribe(event => {
        this.canEditProfile = event;
      }),
    );
    this.getExperiences();
    this.formInit(this.cetrificates);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Get all experiences
   * Filtered them by system-setrificate-id
   */
  getExperiences() {
    this.profileService.trainer
      .pipe(
        tap((trainer: ProfileEntity) => {
          this.trainer = trainer;
        }),
        map((trainer: ProfileEntity) => {
          return trainer.experiences;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result: Experience[]) => {
        this.cetrificates = result;
      });
  }

  /**
   * Generate form from certificates list
   */
  formInit(cetrificates: Experience[]) {
    const cetrificatesControls = cetrificates.map((certificate: Experience) => {
      return new UntypedFormGroup({
        id: new UntypedFormControl(certificate.id, [Validators.required]),
        title: new UntypedFormControl(certificate.title, [Validators.required]),
      });
    });

    this.certificatesFormArray = new UntypedFormArray(cetrificatesControls);

    this.certificatesFormArray.controls.forEach((control: UntypedFormGroup) => {
      this.listenCertificateChanges(control);
    });

    // if there are no any certificates we need add one
    if (this.certificatesFormArray.length < 1) {
      this.addCertificate();
    }
  }

  /**
   * Get certificate ID value
   */
  getCertificateIdValue(certificate: UntypedFormGroup) {
    return certificate.get('id').value;
  }

  /**
   * Get certificate title control
   */
  getCertificateTitleControl(certificate: UntypedFormGroup) {
    return certificate.get('title');
  }

  /**
   * Add certificate
   */
  addCertificate() {
    const profileId = this.trainer.id.toString();

    const newCertificateGroup = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      title: new UntypedFormControl('', [Validators.required]),
    });

    this.certificatesFormArray.push(newCertificateGroup);

    // add listener
    this.listenCertificateChanges(newCertificateGroup);
  }

  /**
   * Add listener to certificate
   */
  listenCertificateChanges(certificateGroup: UntypedFormGroup) {
    certificateGroup.valueChanges
      .pipe(
        takeWhile(() => {
          // trick for unsubscribe from this FormGroup by takeWhile
          return !certificateGroup.contains('remove');
        }),
        filter(() => {
          return certificateGroup.valid;
        }),
        debounceTime(500),
        takeUntil(this.destroy$),
      )
      .subscribe((result: any) => {
        if (Number.isInteger(result.id)) {
          this.updateCertificate(result, certificateGroup);
        } else {
          this.createCertificate(result, certificateGroup);
        }
      });
  }

  /**
   * Update certificate data
   */
  updateCertificate(certificateData, certificateGroup: UntypedFormGroup) {
    const formData = this.getFormData(certificateData);

    this.certificateService.update(this.formName, formData, certificateData.id)
      .pipe(
        finalize(() => {
          certificateGroup.enable({
            emitEvent: false,
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // save new certificates list
        const extractedData = this.extractData(this.certificatesFormArray);
        this.profileService.setExperience(extractedData);
      }, (error) => {
        this.handleErrorResponse(error);
      });
  }

  /**
   * Update certificate data
   */
  createCertificate(certificateData, certificateGroup: UntypedFormGroup) {
    const formData = this.getFormData(certificateData);

    this.certificateService.create(this.formName, formData)
      .pipe(
        finalize(() => {
          certificateGroup.enable({
            emitEvent: false,
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result: Experience) => {
        // set id for certificateGroup
        certificateGroup.patchValue({
          id: result.id,
        }, {
          emitEvent: false,
        });
        // save new certificates list
        const extractedData = this.extractData(this.certificatesFormArray);
        this.profileService.setExperience(extractedData);
      }, (error) => {
        this.handleErrorResponse(error);
      });
  }

  /**
   * Remove sertificate
   */
  removeCertificate(index: number, certificateGroup: UntypedFormGroup) {
    const id = this.getCertificateIdValue(certificateGroup);

    // trick for unsubscribe from this FormGroup by takeWhile
    certificateGroup.setControl('remove', new UntypedFormControl());

    if (id === null) {
      this.removeCertificateByIndex(index);
    } else {
      this.removeCertificateById(certificateGroup, id, index);
    }
  }

  /**
   * Remove sertificate by index from client
   */
  removeCertificateByIndex(index) {
    this.certificatesFormArray.removeAt(index);

    // if there are no any certificates we need add one
    if (this.certificatesFormArray.length < 1) {
      this.addCertificate();
    }
  }

  /**
   * Remove sertificate by ID from server
   * if successfull then remove on client
   */
  removeCertificateById(certificateGroup: UntypedFormGroup, id, index) {
    certificateGroup.disable({
      emitEvent: false,
    });

    this.certificateService.remove(this.formName, id)
      .pipe(
        finalize(() => {
          certificateGroup.enable({
            emitEvent: false,
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.certificatesFormArray.removeAt(index);
        // save new certificates list
        const extractedData = this.extractData(this.certificatesFormArray);
        this.profileService.setExperience(extractedData);

        // if there are no any certificates we need add one
        if (this.certificatesFormArray.length < 1) {
          this.addCertificate();
        }
      }, (error) => {
        this.handleErrorResponse(error);
      });
  }

  /**
   * Get form data for certificate
   */
  getFormData(certificate) {
    const { id, title } = certificate;
    const formData: any = {};

    if (!Number.isInteger(id)) {
      formData.profile_id = this.trainer.id;
    }

    if (title) {
      formData.title = title;
    }

    return formData;
  }

  /**
   * Handle http errors
   */
  handleErrorResponse(errorResponse: HttpErrorResponse) {
    const { data, message, code } = errorResponse.error;

    console.error(errorResponse.error);
  }

  /**
   * Extract data for save it to global user
   */
  extractData(formArray: UntypedFormArray): Experience[] {
    const certificates: Experience[] = formArray.controls
      .map((formGroup: UntypedFormGroup): Experience => {
        return {
          id: formGroup.get('id').value,
          title: formGroup.get('title').value || '',
        };
      });

    return certificates;
  }

}
